/**
 * Unified AI Agent Service
 * 
 * Central orchestration layer for all AI assistants in the system.
 * Ensures all 6 AI assistants have consistent Graph API and Agentics access.
 * 
 * Assistants:
 * 1. AIChat.tsx
 * 2. StructuredAIChat.tsx
 * 3. emailAI.ts (via AIChat.enhanced.tsx)
 * 4. gpt5Orchestrator.ts
 * 5. gpt5NanoAgent.ts
 * 6. structuredEmailAI.ts
 */

import { blink } from '@/lib/blink';
import { tracedEmailActions } from './tracedEmailActions';
import { gpt5Orchestrator } from './gpt5Orchestrator';
import { gpt5NanoAgent } from './gpt5NanoAgent';
import { structuredEmailAI } from './structuredEmailAI';
import { graphApiAgent } from './graphApiAgents';



/**
 * Unified AI Agent Service
 * Provides consistent interface across all AI assistants for:
 * - Graph API operations
 * - Email management
 * - Calendar management
 * - Agentics execution
 */
export class UnifiedAIAgentService {
  private userId: string | null = null;
  private accessToken: string | null = null;

  /**
   * Initialize service with user context
   */
  async initialize(userId: string): Promise<void> {
    this.userId = userId;
    
    // Initialize all assistant services with user context
    // CRITICAL: Use tracedEmailActions for consistent API tracing across all assistants
    tracedEmailActions.setUserId(userId);
    structuredEmailAI.setUserId(userId);
    
    // Retrieve user's access token for Graph API
    try {
      const accounts = await blink.db.emailAccounts.list({
        where: { userId: userId },
        limit: 1
      });
      
      if (accounts && accounts.length > 0) {
        this.accessToken = accounts[0].accessToken as string;
        graphApiAgent.setAccessToken(this.accessToken);
      }
    } catch (error) {
      console.error('Failed to initialize Graph API access:', error);
    }
  }

  /**
   * Get initialization status
   */
  getStatus(): {
    isInitialized: boolean;
    userId: string | null;
    hasGraphApiAccess: boolean;
  } {
    return {
      isInitialized: !!this.userId,
      userId: this.userId,
      hasGraphApiAccess: !!this.accessToken
    };
  }

  // ==================== EMAIL OPERATIONS ====================

  /**
   * Send email
   * Available in: All 6 assistants
   * UNIFIED: All calls go through tracedEmailActions for consistent API tracing
   */
  async sendEmail(params: {
    to: string[];
    cc?: string[];
    bcc?: string[];
    subject: string;
    body: string;
    htmlBody?: string;
    importance?: 'high' | 'normal' | 'low';
  }): Promise<{ messageId: string; success: boolean }> {
    if (!this.userId) throw new Error('Service not initialized');
    return await tracedEmailActions.sendEmail(params);
  }

  /**
   * Search emails
   * Available in: All 6 assistants
   * UNIFIED: All calls go through tracedEmailActions for consistent API tracing
   */
  async searchEmails(params: {
    query: string;
    from?: string;
    to?: string;
    subject?: string;
    hasAttachment?: boolean;
    beforeDate?: Date;
    afterDate?: Date;
    isStarred?: boolean;
    maxResults?: number;
  }): Promise<any[]> {
    if (!this.userId) throw new Error('Service not initialized');
    return await tracedEmailActions.search(params);
  }

  /**
   * Reply to email
   * Available in: All 6 assistants
   * UNIFIED: All calls go through tracedEmailActions for consistent API tracing
   */
  async replyToEmail(params: {
    messageId: string;
    body: string;
    replyAll?: boolean;
  }): Promise<{ success: boolean }> {
    if (!this.userId) throw new Error('Service not initialized');
    await tracedEmailActions.reply(params.messageId, params.body, params.replyAll ?? false);
    return { success: true };
  }

  /**
   * Forward email
   * Available in: All 6 assistants
   * UNIFIED: All calls go through tracedEmailActions for consistent API tracing
   */
  async forwardEmail(params: {
    messageId: string;
    to: string[];
    message?: string;
  }): Promise<{ success: boolean }> {
    if (!this.userId) throw new Error('Service not initialized');
    await tracedEmailActions.forward(params.messageId, params.to, params.message);
    return { success: true };
  }

  /**
   * Archive email
   * Available in: All 6 assistants
   * UNIFIED: All calls go through tracedEmailActions for consistent API tracing
   */
  async archiveEmail(messageId: string): Promise<{ success: boolean }> {
    if (!this.userId) throw new Error('Service not initialized');
    await tracedEmailActions.archive(messageId);
    return { success: true };
  }

  /**
   * Delete/trash email
   * Available in: All 6 assistants
   * UNIFIED: All calls go through tracedEmailActions for consistent API tracing
   */
  async deleteEmail(messageId: string): Promise<{ success: boolean }> {
    if (!this.userId) throw new Error('Service not initialized');
    await tracedEmailActions.deleteEmail(messageId);
    return { success: true };
  }

  /**
   * Star email
   * Available in: All 6 assistants
   * UNIFIED: Pass through to emailActions (internal operation, no API call)
   */
  async starEmail(messageId: string, isStar: boolean = true): Promise<{ success: boolean }> {
    if (!this.userId) throw new Error('Service not initialized');
    await tracedEmailActions.star(messageId, isStar);
    return { success: true };
  }

  /**
   * Mark email as read/unread
   * Available in: All 6 assistants
   * UNIFIED: Pass through to emailActions (internal operation, no API call)
   */
  async markEmailRead(messageId: string, isRead: boolean): Promise<{ success: boolean }> {
    if (!this.userId) throw new Error('Service not initialized');
    await tracedEmailActions.markRead(messageId, isRead);
    return { success: true };
  }

  /**
   * Create email draft
   * Available in: All 6 assistants
   * UNIFIED: All calls go through tracedEmailActions for consistent API tracing
   */
  async createDraft(params: {
    to: string[];
    cc?: string[];
    bcc?: string[];
    subject: string;
    body: string;
  }): Promise<{ draftId: string }> {
    if (!this.userId) throw new Error('Service not initialized');
    const result = await tracedEmailActions.createDraft(params);
    return { draftId: result.id };
  }

  /**
   * Snooze email
   * Available in: All 6 assistants
   * UNIFIED: Pass through to emailActions (internal operation, no API call)
   */
  async snoozeEmail(messageId: string, until: Date): Promise<{ success: boolean }> {
    if (!this.userId) throw new Error('Service not initialized');
    await tracedEmailActions.snooze(messageId, until);
    return { success: true };
  }

  /**
   * Block sender
   * Available in: All 6 assistants
   * UNIFIED: Pass through to emailActions (internal operation, no API call)
   */
  async blockSender(messageId: string): Promise<{ success: boolean }> {
    if (!this.userId) throw new Error('Service not initialized');
    await tracedEmailActions.blockSender(messageId);
    return { success: true };
  }

  // ==================== CALENDAR OPERATIONS ====================

  /**
   * Create calendar event
   * Available in: All 6 assistants (via unifiedCalendarService)
   */
  async createCalendarEvent(params: {
    title: string;
    description?: string;
    start: Date;
    end: Date;
    attendees?: string[];
    location?: string;
    isOnlineMeeting?: boolean;
    reminders?: Array<{ method: string; minutesBefore: number }>;
  }): Promise<{ eventId: string; success: boolean }> {
    if (!this.userId) throw new Error('Service not initialized');
    const { unifiedCalendarService } = await import('./calendar/unifiedCalendarService');
    const event = await unifiedCalendarService.createEvent(params);
    return { eventId: event.id, success: true };
  }

  /**
   * Search calendar events
   * Available in: All 6 assistants
   */
  async searchCalendarEvents(params: {
    query?: string;
    start: Date;
    end: Date;
    maxResults?: number;
  }): Promise<any[]> {
    if (!this.userId) throw new Error('Service not initialized');
    const { unifiedCalendarService } = await import('./calendar/unifiedCalendarService');
    return await unifiedCalendarService.getEventsByDateRange(params.start, params.end, params.query);
  }

  /**
   * Update calendar event
   * Available in: All 6 assistants
   */
  async updateCalendarEvent(params: {
    eventId: string;
    title?: string;
    description?: string;
    start?: Date;
    end?: Date;
    attendees?: string[];
    location?: string;
  }): Promise<{ success: boolean }> {
    if (!this.userId) throw new Error('Service not initialized');
    const { unifiedCalendarService } = await import('./calendar/unifiedCalendarService');
    await unifiedCalendarService.updateEvent(params.eventId, params);
    return { success: true };
  }

  /**
   * Delete calendar event
   * Available in: All 6 assistants
   */
  async deleteCalendarEvent(eventId: string): Promise<{ success: boolean }> {
    if (!this.userId) throw new Error('Service not initialized');
    const { unifiedCalendarService } = await import('./calendar/unifiedCalendarService');
    await unifiedCalendarService.deleteEvent(eventId);
    return { success: true };
  }

  // ==================== AGENTICS OPERATIONS ====================

  /**
   * Execute complex user intent using GPT-5 Nano Agent
   * Available in: All 6 assistants
   */
  async executeUserIntent(userMessage: string): Promise<{
    success: boolean;
    action: string;
    confidence: number;
    result: any;
    reasoning: string;
    warnings?: string[];
    error?: string;
  }> {
    if (!this.userId) throw new Error('Service not initialized');
    
    try {
      const response = await gpt5NanoAgent.processUserIntent(userMessage);
      
      if (!response.validation.isValid) {
        return {
          success: false,
          action: response.action.type,
          confidence: response.action.confidence,
          result: null,
          reasoning: response.reasoning,
          warnings: response.validation.warnings,
          error: response.validation.errors.join('; ')
        };
      }

      const result = await gpt5NanoAgent.executeAction(response.action);

      return {
        success: true,
        action: response.action.type,
        confidence: response.action.confidence,
        result,
        reasoning: response.reasoning,
        warnings: response.validation.warnings
      };
    } catch (error) {
      return {
        success: false,
        action: 'error',
        confidence: 0,
        result: null,
        reasoning: 'Failed to execute intent',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Execute structured email automation
   * Available in: structuredEmailAI.ts and all assistants
   */
  async executeEmailAutomation(userMessage: string): Promise<any> {
    if (!this.userId) throw new Error('Service not initialized');
    return await structuredEmailAI.executeUserIntent(userMessage);
  }

  /**
   * Execute multi-step orchestrated workflow
   * Available in: gpt5Orchestrator.ts and all assistants
   */
  async executeOrchestrationWorkflow(prompt: string): Promise<string> {
    const plan = await gpt5Orchestrator.planExecution(prompt);
    return await gpt5Orchestrator.execute(plan);
  }

  // ==================== GRAPH API DIRECT ACCESS ====================

  /**
   * Direct Graph API operation execution
   * Available for advanced use cases in all assistants
   */
  async executeGraphApiOperation(params: {
    operation: string;
    userIntent: string;
    parameters: Record<string, any>;
  }): Promise<any> {
    if (!this.accessToken) throw new Error('No Graph API access token available');
    return await graphApiAgent.execute(
      params.operation,
      params.userIntent,
      params.parameters
    );
  }

  /**
   * Direct Graph API operation (no AI intermediary)
   * Available for critical operations
   */
  async executeGraphApiDirect(params: {
    operation: string;
    parameters: Record<string, any>;
  }): Promise<any> {
    if (!this.accessToken) throw new Error('No Graph API access token available');
    return await graphApiAgent.executeDirect(
      params.operation,
      params.parameters
    );
  }

  // ==================== AI ANALYSIS ====================

  /**
   * Analyze email using structured AI
   * Available in: emailAI.ts and all assistants
   */
  async analyzeEmail(emailId: string): Promise<any> {
    if (!this.userId) throw new Error('Service not initialized');
    return await structuredEmailAI.analyzeEmail(emailId);
  }

  /**
   * Generate reply suggestions
   * Available in: emailAI.ts and all assistants
   */
  async generateReplySuggestions(emailId: string): Promise<string[]> {
    if (!this.userId) throw new Error('Service not initialized');
    return await structuredEmailAI.generateReplySuggestions(emailId);
  }

  /**
   * Generate email from user intent
   * Available in: emailAI.ts and all assistants
   */
  async generateEmailFromIntent(intent: string): Promise<{
    to: string[];
    cc?: string[];
    subject: string;
    body: string;
  }> {
    if (!this.userId) throw new Error('Service not initialized');
    return await structuredEmailAI.generateEmailFromIntent(intent);
  }

  // ==================== AUTOMATION RULES ====================

  /**
   * Create automation rule
   * Available in: structuredEmailAI.ts and all assistants
   */
  async createAutomationRule(description: string): Promise<any> {
    if (!this.userId) throw new Error('Service not initialized');
    return await structuredEmailAI.createAutomationRule(description);
  }

  /**
   * Apply automation rule to email
   * Available in: structuredEmailAI.ts and all assistants
   */
  async applyAutomationRule(emailId: string, ruleId: string): Promise<boolean> {
    if (!this.userId) throw new Error('Service not initialized');
    return await structuredEmailAI.applyRule(emailId, ruleId);
  }

  /**
   * Get active automation rules
   * Available in: structuredEmailAI.ts and all assistants
   */
  getActiveAutomationRules(): any[] {
    return structuredEmailAI.getActiveRules();
  }
}

// Singleton instance
export const unifiedAIAgentService = new UnifiedAIAgentService();

/**
 * Helper function to initialize service with user
 */
export async function initializeAIAgentService(userId: string): Promise<UnifiedAIAgentService> {
  await unifiedAIAgentService.initialize(userId);
  return unifiedAIAgentService;
}
